// Evidence-thumbnail table cell (design pull, 2026-08-12) — replaces the
// SLA countdown column on Observaciones' table (founder decision:
// documentation-richness over deadline-triage on this view). Not a
// button: the enclosing <tr> is the click target (row-link pattern,
// matching Tiendas/Proyectos/DetalleProyecto), so a nested interactive
// element here would fight the row's own handler for the click.
//
// Renders real thumbnails when `thumbUrls` arrive; when the wire carries
// none yet (older core-api, cached bundle) it falls back to icon
// placeholders so the cell reads the same either way. Placeholders are
// never mixed with real thumbnails: `count` spans audio and notes too
// (see the wire-schema comment on evidenceThumbUrls), so a grey tile next
// to a real photo would claim a second photo that may not exist. Each
// <img> degrades to a placeholder on its own error — a deleted or
// unreachable object must never leak a broken-image glyph into a dense
// table row.

import { useState } from "react";
import { Icon } from "./icons";

// Layout cap, not the source of truth: the server owns the strip size
// (ENTRY_THUMBS_LIMIT in core-api's facility-dashboard route). This only
// protects the kanban card footer and the .dtable cell from a wider
// strip if the server knob is ever raised without a design pass.
const THUMB_LIMIT = 2;

function PlaceholderThumb({ broken = false }: { broken?: boolean }) {
  return (
    <span className={`ev-cell__thumb${broken ? " ev-cell__thumb--broken" : ""}`} aria-hidden="true">
      <Icon.Photo s={16} />
    </span>
  );
}

function Thumb({ src }: { src: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <PlaceholderThumb broken />;
  return (
    <span className="ev-cell__thumb" aria-hidden="true">
      <img
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        width={32}
        height={32}
        onError={() => setFailed(true)}
      />
    </span>
  );
}

export function EvidenciaCell({ count, thumbUrls = [] }: { count: number; thumbUrls?: string[] }) {
  if (count === 0) {
    return <span className="ev-cell ev-cell--note ev-cell__n">Sin fotos</span>;
  }
  const thumbs = thumbUrls.slice(0, THUMB_LIMIT);
  const placeholderCount = thumbs.length > 0 ? 0 : Math.min(count, THUMB_LIMIT);
  return (
    <span className="ev-cell">
      <span className="ev-cell__stack">
        {thumbs.map((src, i) => (
          <Thumb key={`${i}-${src}`} src={src} />
        ))}
        {Array.from({ length: placeholderCount }, (_, i) => (
          <PlaceholderThumb key={`ph-${i}`} />
        ))}
      </span>
      <span className="ev-cell__n">
        {count} foto{count === 1 ? "" : "s"}
      </span>
    </span>
  );
}
